import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { getPunditPurohitAuthHeaders, getStoredPunditPurohitUser } from '../utils/auth';
import { DevoteeTestimonialForm } from '../components/DevoteeTestimonials';

const formatDate = (date) => {
  if (!date) return 'Date pending';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const safeJsonParse = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatList = (items = []) => (items.length ? items.join(', ') : 'None selected');

const formatQuantityList = (items = []) => (
  items.length
    ? items.map((item) => `${item.name} x ${item.quantity}`).join(', ')
    : 'None selected'
);

const formatMoney = (value, currency = 'USD') => `${currency} ${Number(value || 0).toFixed(2)}`;

const hasBookingDetails = (item = {}) => (
  Boolean(item.selectedTier?.duration)
  || Boolean(item.requiredItems?.length)
  || Boolean(item.punditBringItems?.length)
  || Boolean(item.userArrangedItems?.length)
  || Boolean(item.prasadamSelections?.length)
  || Boolean(item.addonSelections?.length)
);

const BookingDetailSummary = ({ item }) => {
  const requiredItems = item.requiredItems || [];
  const punditBringItems = item.punditBringItems || [];
  const userArrangedItems = item.userArrangedItems || [];
  const prasadamSelections = item.prasadamSelections || [];
  const addonSelections = item.addonSelections || [];
  const hasDetails = requiredItems.length
    || punditBringItems.length
    || userArrangedItems.length
    || prasadamSelections.length
    || addonSelections.length
    || item.selectedTier?.duration;

  if (!hasDetails) return null;

  return (
    <div className="booking-detail-summary">
      {item.selectedTier?.duration && (
        <div>
          <span>Duration</span>
          <strong>{item.selectedTier.duration}</strong>
        </div>
      )}
      <div>
        <span>Pundit brings</span>
        <strong>{formatList(punditBringItems)}</strong>
      </div>
      <div>
        <span>User arranges</span>
        <strong>{formatList(userArrangedItems)}</strong>
      </div>
      {requiredItems.length > 0 && (
        <div>
          <span>Required items</span>
          <strong>{formatList(requiredItems)}</strong>
        </div>
      )}
      <div>
        <span>Prasadam</span>
        <strong>{formatQuantityList(prasadamSelections)}</strong>
      </div>
      <div>
        <span>Add-ons</span>
        <strong>{formatQuantityList(addonSelections)}</strong>
      </div>
      <div>
        <span>Price split</span>
        <strong>
          Pooja {formatMoney(item.basePrice || item.price || 0)}
          {prasadamSelections.length > 0 && `, Prasadam ${formatMoney(item.prasadamTotal || 0)}`}
          {addonSelections.length > 0 && `, Add-ons ${formatMoney(item.addonTotal || 0)}`}
        </strong>
      </div>
    </div>
  );
};

const BookingSummaryCard = ({ item, currency = 'USD', mode = 'customer', onViewDetails }) => {
  const date = item.bookingDate || item.booking_date;
  const time = item.bookingTime || item.booking_time;
  const qty = item.quantity || 1;
  const price = item.itemTotal || Number(item.price || 0) * qty;
  const punditBringItems = item.punditBringItems || [];
  const userArrangedItems = item.userArrangedItems || [];
  const prasadamSelections = item.prasadamSelections || [];
  const addonSelections = item.addonSelections || [];

  return (
    <div className="booking-summary-card">
      <div className="booking-summary-main">
        <div>
          <h3>{item.name}</h3>
          <p className="booking-summary-meta">
            {formatDate(date)} {time || 'Time pending'} | Qty: {qty} | {formatMoney(price, currency)}
          </p>
        </div>
        <button type="button" className="order-details-toggle" onClick={onViewDetails}>
          View details
        </button>
      </div>

      <div className="booking-summary-lines">
        <p><strong>Pundit brings:</strong> {formatList(punditBringItems)}</p>
        <p><strong>User arranges:</strong> {formatList(userArrangedItems)}</p>
        {prasadamSelections.length > 0 && <p><strong>Prasadam:</strong> {formatQuantityList(prasadamSelections)}</p>}
        {addonSelections.length > 0 && <p><strong>Add-ons:</strong> {formatQuantityList(addonSelections)}</p>}
        {mode === 'pundit' && item.selectedTier?.duration && <p><strong>Duration:</strong> {item.selectedTier.duration}</p>}
      </div>
    </div>
  );
};

const BookingDetailsModal = ({ open, detail, onClose }) => {
  if (!open || !detail?.item) return null;

  const { item, order, address, mode } = detail;
  const date = item.bookingDate || item.booking_date;
  const time = item.bookingTime || item.booking_time;
  const qty = item.quantity || 1;
  const currency = order?.currency || item.currency || 'USD';
  const price = item.itemTotal || Number(item.price || 0) * qty;

  return (
    <div className="modal-overlay order-detail-modal-overlay">
      <motion.div
        className="order-detail-modal"
        initial={{ opacity: 0, y: 18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.98 }}
        transition={{ duration: 0.2 }}
      >
        <div className="order-detail-modal-header">
          <div>
            <span>{mode === 'pundit' ? 'Pundit booking details' : 'Booking details'}</span>
            <h3>{item.name}</h3>
            <p>
              {formatDate(date)} {time || 'Time pending'} | Qty: {qty} | {formatMoney(price, currency)}
            </p>
          </div>
          <button type="button" className="auth-modal-close" onClick={onClose}>Close</button>
        </div>

        <div className="order-detail-modal-body">
          <div className="order-detail-context">
            <div>
              <span>Order</span>
              <strong>#{order?.orderId || order?.order_number || item.order_number || 'Pending'}</strong>
            </div>
            <div>
              <span>Location</span>
              <strong>{address?.address || 'Service location pending'}</strong>
            </div>
            <div>
              <span>{mode === 'pundit' ? 'Customer' : 'Booked on'}</span>
              <strong>
                {mode === 'pundit'
                  ? (item.user_contact_name || item.user_name || 'Customer')
                  : (order?.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Booking date pending')}
              </strong>
            </div>
          </div>

          <BookingDetailSummary item={item} />
        </div>
      </motion.div>
    </div>
  );
};

const ViewOrders = () => {
  const [orders, setOrders] = useState([]);
  const [availableBookings, setAvailableBookings] = useState([]);
  const [calendar, setCalendar] = useState({ userBookings: [], punditBookings: [] });
  const [detailModal, setDetailModal] = useState(null);
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [reservingId, setReservingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const user = getStoredPunditPurohitUser();
  const isPundit = Boolean(user?.isPundit);
  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const getStatus = (order) => {
    const today = new Date().toISOString().split('T')[0];
    const dates = order.items.map(i => i.bookingDate).filter(Boolean);
    const latestDate = dates.length > 0 ? dates.sort().reverse()[0] : '';
    return latestDate && latestDate < today ? 'Completed' : 'Upcoming';
  };

  const fetchOrders = useCallback(() => {
    setLoading(true);
    axios
      .get(`${apiBase}/api/punditpurohit/orders`, { headers: getPunditPurohitAuthHeaders() })
      .then((res) => setOrders(res.data))
      .catch((err) => {
        console.error('Unable to load orders', err);
        setOrders([]);
      })
      .finally(() => setLoading(false));
  }, [apiBase]);

  const fetchPunditData = useCallback(() => {
    if (!isPundit) return;

    axios
      .get(`${apiBase}/api/punditpurohit/pundit/bookings/available`, { headers: getPunditPurohitAuthHeaders() })
      .then((res) => setAvailableBookings(res.data))
      .catch((err) => {
        console.error('Unable to load available pundit bookings', err);
        setAvailableBookings([]);
      });
  }, [apiBase, isPundit]);

  const fetchCalendar = useCallback(() => {
    axios
      .get(`${apiBase}/api/punditpurohit/calendar`, { headers: getPunditPurohitAuthHeaders() })
      .then((res) => setCalendar(res.data))
      .catch((err) => {
        console.error('Unable to load calendar', err);
        setCalendar({ userBookings: [], punditBookings: [] });
      });
  }, [apiBase]);

  useEffect(() => {
    fetchOrders();
    fetchPunditData();
    fetchCalendar();
  }, [fetchOrders, fetchPunditData, fetchCalendar]);

  const reserveBooking = (itemId) => {
    setReservingId(itemId);
    axios
      .put(`${apiBase}/api/punditpurohit/pundit/bookings/${itemId}/reserve`, {}, { headers: getPunditPurohitAuthHeaders() })
      .then(() => {
        alert('Booking reserved. Email notifications have been sent.');
        fetchPunditData();
        fetchCalendar();
      })
      .catch((err) => alert(err?.response?.data?.message || 'Unable to reserve booking.'))
      .finally(() => setReservingId(null));
  };

  const cancelOrder = (orderNumber) => {
    if (!window.confirm('Cancel this booking?')) return;
    setCancellingId(orderNumber);
    axios
      .put(`${apiBase}/api/punditpurohit/orders/${orderNumber}/cancel`, {}, { headers: getPunditPurohitAuthHeaders() })
      .then(() => {
        alert('Booking cancelled.');
        fetchOrders();
        fetchCalendar();
      })
      .catch((err) => alert(err?.response?.data?.message || 'Unable to cancel booking.'))
      .finally(() => setCancellingId(null));
  };

  const cancelReservation = (itemId) => {
    if (!window.confirm('Cancel your reservation for this booking?')) return;
    setCancellingId(itemId);
    axios
      .put(`${apiBase}/api/punditpurohit/pundit/bookings/${itemId}/cancel-reservation`, {}, { headers: getPunditPurohitAuthHeaders() })
      .then(() => {
        alert('Reservation cancelled.');
        fetchPunditData();
        fetchCalendar();
      })
      .catch((err) => alert(err?.response?.data?.message || 'Unable to cancel reservation.'))
      .finally(() => setCancellingId(null));
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    const status = getStatus(order);
    return status.toLowerCase() === filter;
  });

  const normalizeCalendarBooking = (booking, calendarType) => ({
    ...safeJsonParse(booking.metadata_json, {}),
    ...booking,
    calendarType,
  });

  const calendarRows = [
    ...calendar.userBookings.map((booking) => normalizeCalendarBooking(booking, 'My Pooja')),
    ...calendar.punditBookings.map((booking) => normalizeCalendarBooking(booking, 'Reserved Service')),
  ].sort((a, b) => `${a.booking_date || ''}${a.booking_time || ''}`.localeCompare(`${b.booking_date || ''}${b.booking_time || ''}`));

  return (
    <div className="orders-page-wrapper">
      <div className="orders-container wide">
        <header className="orders-header">
          <h1>Bookings</h1>
          <div className="filter-scroll">
            <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>My Bookings</button>
            {isPundit && <button className={tab === 'available' ? 'active' : ''} onClick={() => setTab('available')}>Available for Pundits</button>}
            <button className={tab === 'calendar' ? 'active' : ''} onClick={() => setTab('calendar')}>Calendar</button>
          </div>
        </header>

        {tab === 'orders' && (
          <>
            {!loading && <DevoteeTestimonialForm orders={orders.filter((order) => order.status !== 'cancelled')} />}
            <div className="filter-scroll compact">
              {['all', 'upcoming', 'completed'].map((f) => (
                <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {loading ? <p>Loading bookings...</p> : (
              <div className="orders-grid">
                <AnimatePresence mode="popLayout">
                  {filteredOrders.length === 0 ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="empty-state">
                      <p>No bookings found in this category.</p>
                    </motion.div>
                  ) : (
                    filteredOrders.map((order) => (
                      <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} key={order.orderId} className={`modern-order-card ${getStatus(order).toLowerCase()}`}>
                        <div className="card-top">
                          <div className="order-main-meta">
                            <span className="order-id">#{order.orderId}</span>
                            <span className="booking-mode-tag">{order.bookingMode}</span>
                          </div>
                          <span className="status-pill">{order.status === 'cancelled' ? 'Cancelled' : getStatus(order)}</span>
                        </div>

                        {order.address && (
                          <div className="order-address-bar">
                            <div className="address-icon">LOC</div>
                            <div className="address-details">
                              <strong>Service at {order.address.type}</strong>
                              <p>{order.address.address}</p>
                            </div>
                          </div>
                        )}

                        <div className="card-body">
                          {order.items.map((item, i) => {
                            const hasDetails = hasBookingDetails(item);
                            return (
                            <div key={`${order.orderId}-${item.id || i}`} className="pooja-entry">
                              <BookingSummaryCard
                                item={item}
                                currency={order.currency}
                                mode="customer"
                                onViewDetails={() => setDetailModal({
                                  item,
                                  order,
                                  address: order.address,
                                  mode: 'customer',
                                })}
                              />
                              <div className="pooja-status-strip">
                                <span>{item.punditCount} Pundit</span>
                                <span>{item.status === 'cancelled' ? 'Cancelled' : item.status === 'reserved' ? 'Pundit assigned' : 'Waiting for pundit'}</span>
                                {item.selectedTier?.duration && <span>{item.selectedTier.duration}</span>}
                                {!hasDetails && <span>Standard booking</span>}
                              </div>
                            </div>
                          );
                          })}
                        </div>

                        <div className="card-bottom">
                          <div className="booked-timestamp">
                            Booked on {new Date(order.createdAt).toLocaleDateString()}
                            {!order.canCancel && order.status !== 'cancelled' && (
                              <small className="cancel-note">Cancellation closes 24 hours before schedule.</small>
                            )}
                          </div>
                          <div className="price-summary">
                            <span className="label">Total Amount</span>
                            <span className="value">${order.finalTotal}</span>
                          </div>
                        </div>
                        {order.status !== 'cancelled' && (
                          <button
                            className="pp-secondary-action danger"
                            disabled={!order.canCancel || cancellingId === order.orderId}
                            onClick={() => cancelOrder(order.orderId)}
                          >
                            {cancellingId === order.orderId ? 'Cancelling...' : 'Cancel Booking'}
                          </button>
                        )}
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            )}
          </>
        )}

        {tab === 'available' && isPundit && (
          <div className="orders-grid">
            {availableBookings.length === 0 ? (
              <div className="empty-state"><p>No open bookings are available right now.</p></div>
            ) : (
              availableBookings.map((booking) => {
                const address = safeJsonParse(booking.address_json, {});
                const hasDetails = hasBookingDetails(booking);
                return (
                  <div key={booking.id} className="modern-order-card">
                    <div className="card-top">
                      <div className="order-main-meta">
                        <span className="order-id">#{booking.order_number}</span>
                        <span className="booking-mode-tag">{booking.booking_mode}</span>
                      </div>
                      <span className="status-pill">Open</span>
                    </div>
                    <div className="order-address-bar">
                      <div className="address-icon">LOC</div>
                      <div className="address-details">
                        <strong>{address.type || 'Service Location'}</strong>
                        <p>{address.address}</p>
                      </div>
                    </div>
                    <div className="pp-contact-box">
                      <strong>Customer</strong>
                      <p>{booking.user_contact_name || booking.user_name || 'Name unavailable'}</p>
                      <p>{booking.user_contact_mobile || 'Mobile unavailable'}</p>
                      <p>{booking.user_contact_email || booking.user_email}</p>
                    </div>
                    <div className="pooja-entry">
                      <BookingSummaryCard
                        item={booking}
                        currency={booking.currency || 'USD'}
                        mode="pundit"
                        onViewDetails={() => setDetailModal({
                          item: booking,
                          order: booking,
                          address,
                          mode: 'pundit',
                        })}
                      />
                      <div className="pooja-status-strip">
                        <span>{formatDate(booking.booking_date)}</span>
                        <span>{booking.booking_time || 'Time pending'}</span>
                        {booking.selectedTier?.duration && <span>{booking.selectedTier.duration}</span>}
                        {!hasDetails && <span>Standard booking</span>}
                      </div>
                    </div>
                    <button className="login-submit-btn" disabled={reservingId === booking.id} onClick={() => reserveBooking(booking.id)}>
                      {reservingId === booking.id ? 'Reserving...' : 'Reserve Booking'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === 'calendar' && (
          <div className="pp-calendar-list">
            {calendarRows.length === 0 ? (
              <div className="empty-state"><p>No calendar entries yet.</p></div>
            ) : (
              calendarRows.map((booking) => {
                const address = safeJsonParse(booking.address_json, {});
                const isReservedService = booking.calendarType === 'Reserved Service';
                return (
                  <div key={`${booking.calendarType}-${booking.id}`} className="pp-calendar-row">
                    <div>
                      <strong>{formatDate(booking.booking_date)}</strong>
                      <span>{booking.booking_time || 'Time pending'}</span>
                    </div>
                    <div>
                      <h4>{booking.name}</h4>
                      <p>{booking.calendarType} - #{booking.order_number}</p>
                    </div>
                    <div className="pp-calendar-meta">
                      {isReservedService && (
                        <>
                          <p>{booking.user_contact_name || booking.user_name || 'Customer name unavailable'}</p>
                          <p>{booking.user_contact_mobile || 'Mobile unavailable'}</p>
                          {address.address && <p>{address.address}</p>}
                        </>
                      )}
                      <span className="status-pill">{booking.status}</span>
                      {hasBookingDetails(booking) && (
                        <button
                          type="button"
                          className="order-details-toggle calendar-details-toggle"
                          onClick={() => setDetailModal({
                            item: booking,
                            order: booking,
                            address,
                            mode: isReservedService ? 'pundit' : 'customer',
                          })}
                        >
                          View details
                        </button>
                      )}
                      {isReservedService && (
                        <button
                          className="pp-secondary-action danger"
                          disabled={!booking.canCancelReservation || cancellingId === booking.id}
                          onClick={() => cancelReservation(booking.id)}
                        >
                          {cancellingId === booking.id ? 'Cancelling...' : 'Cancel Reservation'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      <AnimatePresence>
        {detailModal && (
          <BookingDetailsModal
            open={Boolean(detailModal)}
            detail={detailModal}
            onClose={() => setDetailModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ViewOrders;
